<script setup lang="ts">
/**
 * 一体的拾色面板：饱和度/明度方块 + 色相条 + 透明度条 + 十六进制。
 *
 * ## 为什么不用原生 `<input type="color">`
 *
 * 第一版就是用它，结果是**两级嵌套**：色块点开一个浮层，浮层里再点开系统取色器。
 * 三个问题：
 *
 *   · 它**没有 alpha 通道**，透明度只能另找地方 —— 这正是嵌套的来源
 *   · 它是系统模态框，跟画布视觉完全脱节
 *   · 两层交互才能改一个颜色
 *
 * 所以自己画一个。市面上常见的拾色器都是这个形态：
 * 左上角白 → 右下角黑的方块定色调，色相条定色相，透明度条定 alpha。
 *
 * ## ⚠️ 灰色（s = 0）的色相在数学上是未定义的
 *
 * `rgbToHsv` 只能返回 0。所以面板必须**自己记着色相** ——
 * 不然把饱和度拖到 0 再拖回来，颜色会跳到红色。
 * 状态因此放在面板本地，而不是每次从 props 重算。
 */
import { computed, ref, watch } from 'vue'
import { alphaOf, hexToHsv, hsvToHex, isHexColor, toRgbHex, withAlpha } from '../../types/color'

const props = defineProps<{
  value: string
  /** 是否显示透明度条 */
  alpha?: boolean
}>()

const emit = defineEmits<{ (e: 'update', value: string): void }>()

const alphaEnabled = computed(() => Boolean(props.alpha))

/* ── 本地 HSV 状态 ──────────────────────────────────────────── */

const hue = ref(0)
const sat = ref(0)
const val = ref(0)

watch(
  () => props.value,
  (next) => {
    const hsv = hexToHsv(next)
    if (!hsv) return
    // ★ 灰色保留当前色相（见文件头注释）
    if (hsv.s > 0.001) hue.value = hsv.h
    sat.value = hsv.s
    val.value = hsv.v
  },
  { immediate: true },
)

const alpha = computed(() => alphaOf(props.value))
const alphaPercent = computed(() => Math.round(alpha.value * 100))

/** 不含 alpha 的纯色，用来铺色相条 / 透明度条的渐变终点 */
const solidHex = computed(() => hsvToHex({ h: hue.value, s: sat.value, v: val.value }))

function emitHsv(
  next: { h?: number; s?: number; v?: number },
  nextAlpha?: number,
) {
  const h = next.h ?? hue.value
  const s = next.s ?? sat.value
  const v = next.v ?? val.value
  hue.value = h
  sat.value = s
  val.value = v
  emit('update', hsvToHex({ h, s, v }, nextAlpha ?? alpha.value))
}

/* ── 饱和度 / 明度方块 ─────────────────────────────────────── */

const areaEl = ref<HTMLElement | null>(null)
const dragging = ref(false)

const clamp01 = (n: number) => Math.max(0, Math.min(1, n))

function setFromArea(ev: PointerEvent) {
  const el = areaEl.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  if (rect.width === 0 || rect.height === 0) return
  emitHsv({
    s: clamp01((ev.clientX - rect.left) / rect.width),
    v: 1 - clamp01((ev.clientY - rect.top) / rect.height),
  })
}

function onAreaDown(ev: PointerEvent) {
  const el = ev.currentTarget as HTMLElement
  // 指针捕获：拖出方块外面也继续跟手
  el.setPointerCapture?.(ev.pointerId)
  dragging.value = true
  setFromArea(ev)
}

function onAreaMove(ev: PointerEvent) {
  if (dragging.value) setFromArea(ev)
}

function onAreaUp(ev: PointerEvent) {
  dragging.value = false
  ;(ev.currentTarget as HTMLElement).releasePointerCapture?.(ev.pointerId)
}

/* ── 色相 / 透明度 / 十六进制 ───────────────────────────────── */

function onHue(ev: Event) {
  emitHsv({ h: Number((ev.target as HTMLInputElement).value) })
}

function onAlpha(ev: Event) {
  emitHsv({}, Number((ev.target as HTMLInputElement).value) / 100)
}

function onHex(ev: Event) {
  const input = ev.target as HTMLInputElement
  const raw = input.value.trim()
  if (!isHexColor(raw)) {
    input.value = props.value
    return
  }
  // 走 withAlpha 归一化：满不透明会退回 6 位
  emit('update', withAlpha(raw, alphaOf(raw)))
}
</script>

<template>
  <div class="np-colorpicker">
    <div
      ref="areaEl"
      class="np-colorpicker__area"
      :style="{ '--np-picker-hue': `hsl(${hue}, 100%, 50%)` }"
      @pointerdown="onAreaDown"
      @pointermove="onAreaMove"
      @pointerup="onAreaUp"
      @pointercancel="onAreaUp"
    >
      <span
        class="np-colorpicker__thumb"
        :style="{ left: `${sat * 100}%`, top: `${(1 - val) * 100}%` }"
      />
    </div>

    <input
      class="np-colorpicker__slider np-colorpicker__hue"
      type="range"
      min="0"
      max="360"
      step="1"
      :value="Math.round(hue)"
      title="色相"
      @input="onHue"
    />

    <input
      v-if="alphaEnabled"
      class="np-colorpicker__slider np-colorpicker__alpha"
      type="range"
      min="0"
      max="100"
      step="1"
      :value="alphaPercent"
      :style="{ '--np-picker-color': solidHex }"
      :title="`不透明度 ${alphaPercent}%`"
      @input="onAlpha"
    />

    <div class="np-colorpicker__foot">
      <span class="np-colorpicker__preview" :style="{ '--np-picker-color': solidHex, '--np-picker-a': alpha }">
        <i :style="{ background: solidHex, opacity: alpha }" />
      </span>
      <input
        class="np-colorpicker__hex"
        type="text"
        autocomplete="off"
        spellcheck="false"
        :value="props.value"
        @change="onHex"
      />
      <span v-if="alphaEnabled" class="np-colorpicker__percent">{{ alphaPercent }}%</span>
    </div>
  </div>
</template>

/* ══════════════════════════════════════════════════════════════════
   拾色面板的全部样式（从 `theme.css` 向下拆到这里）

   ⚠️ **为什么搬**：这 20 条规则只有本组件用（实测全仓库搜过），
   它又有自己的 SFC —— 样式没理由住在全局表里。

   ⚠️⚠️ **顺手改了名**：原来叫 `.np-picker` / `.np-picker__*`，
   而 `NpNodePicker.vue`（节点选择器）**也叫 `.np-picker`** ——
   那边是 `position: fixed; inset: 0` 的全屏浮层，这边带
   `width: 214px`。全局那条**会外溢到节点选择器上**（实测 `gap: 9px`
   同时命中两边）。现在统一叫 `.np-colorpicker*`，和弹出层
   `.np-color-popover` 一个前缀。
   ══════════════════════════════════════════════════════════════════ */

<style>
.np-colorpicker {
  /*
   * ── 拾色器自己的令牌 ────────────────────────────────────────────
   *
   * ⚠️ **为什么这几个住在这里、而不是全局表**：它们只在「手柄悬在
   * 任意颜色上」这种控件里成立。SV 方块左上角是纯白、右上是纯色、
   * 底下是黑 —— 手柄没有一圈白环 + 一道深色发丝线，**在某些位置就是
   * 看不见**。这是拾色器的结构事实，不是全站语义：全局没有第二个用户，
   * 提上去只会多一个没人说得清含义的令牌。
   *
   * 反例是 `--np-checker-*`：那是「透明色怎么表示」，全仓库都有用户，
   * 所以它归 `theme.css`，这里只引用。
   */
  --np-picker-handle-ring: #fff;
  --np-picker-handle-outline: 0 0 0 1px rgba(0, 0, 0, 0.55);
  /* SV 手柄比滑块手柄多一层投影：它压在渐变上，只有发丝线不够 */
  --np-picker-thumb-shadow: var(--np-picker-handle-outline), 0 1px 4px rgba(0, 0, 0, 0.5);
  /*
   * 色相条 / 透明度条的高度：**轨道、滑块、手柄是一组**。
   * 原来滑块上的 `margin-top: -2px` 是手算的 (12 − 16) / 2 ——
   * 谁改一个高度，手柄就浮到轨道外面。三个值放一起，偏移由它们算。
   */
  --np-picker-strip-h: 16px;
  --np-picker-track-h: 12px;
  --np-picker-thumb-w: 7px;

  /*
   * ── 棋盘格：四层渐变的**唯一**一份 ──────────────────────────────
   *
   * ⚠️ 这四层原来在本文件里写了三遍（透明度条 ×2 + 预览块），颜色还是
   * 写死的 `#3a3a3a` / `#787878`（全局表里另有第四遍）。现在只有这一份，
   * 三处都 `var(--np-picker-checker)` 引过去。
   *
   * `var()` 是**文本替换**，所以这四条能当「一层」拼进图层列表：
   *   background-image: <到当前色的渐变>, var(--np-picker-checker);
   * 展开后正好是「1 个渐变 + 4 个棋盘格 = 5 层」。
   */
  --np-picker-checker:
    linear-gradient(45deg, var(--np-checker-b) 25%, transparent 25%),
    linear-gradient(-45deg, var(--np-checker-b) 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, var(--np-checker-b) 75%),
    linear-gradient(-45deg, transparent 75%, var(--np-checker-b) 75%);
  /* 半个格子：四个图层靠它互相错开半格 */
  --np-picker-checker-half: calc(var(--np-checker-size) * 0.5);
  --np-picker-checker-half-neg: calc(var(--np-checker-size) * -0.5);
  /*
   * ⚠️⚠️ 尺寸 / 位置的**个数必须跟图层一一对应**（README「棋盘格：三个
   * CSS 坑」②：多给一个值就整体错位，棋盘看起来变成条纹）。所以这两份
   * 列表也只在这里写一次 —— 下面三处引用即可，谁都不用再数一遍。
   * 尺寸那份是「4 个棋盘格共用一个尺寸」，引用处再补渐变那层的 `100% 100%`。
   */
  --np-picker-checker-pos:
    0 0,
    0 var(--np-picker-checker-half),
    var(--np-picker-checker-half) var(--np-picker-checker-half-neg),
    var(--np-picker-checker-half-neg) 0;
  --np-picker-checker-sizes:
    var(--np-checker-size) var(--np-checker-size),
    var(--np-checker-size) var(--np-checker-size),
    var(--np-checker-size) var(--np-checker-size),
    var(--np-checker-size) var(--np-checker-size);

  display: flex;
  flex-direction: column;
  gap: 9px;
  width: 214px;
  padding: 11px;
}

.np-colorpicker__area {
  position: relative;
  height: 112px;
  border-radius: var(--np-radius-m, 6px);
  cursor: crosshair;
  /* 指针会被捕获到方块上，禁掉浏览器自己的触摸手势 */
  touch-action: none;
  /*
   * 三层叠出来：
   *   纵向 黑 → 透明   = 明度
   *   横向 白 → 透明   = 饱和度
   *   底色 = 当前色相
   *
   * ⚠️ **黑 / 白这两层保持字面量，别换成令牌**：它们是色彩空间的构造，
   * 不是配色。SV 方块的四个角必须是「纯白（s=0,v=1）· 纯色（s=1,v=1）·
   * 黑（v=0）」—— 换成令牌就等于让主题改掉 HSV 的定义，方块上取到的
   * 颜色跟它显示的位置就对不上了。
   */
  background-image:
    linear-gradient(to top, #000, rgba(0, 0, 0, 0)),
    linear-gradient(to right, #fff, rgba(255, 255, 255, 0)),
    linear-gradient(var(--np-picker-hue, #f00), var(--np-picker-hue, #f00));
}

.np-colorpicker__thumb {
  position: absolute;
  width: 12px;
  height: 12px;
  margin: -6px 0 0 -6px;
  border: 2px solid var(--np-picker-handle-ring);
  border-radius: 50%;
  box-shadow: var(--np-picker-thumb-shadow);
  pointer-events: none;
}

.np-colorpicker__slider {
  width: 100%;
  height: var(--np-picker-strip-h);
  margin: 0;
  border-radius: 3px;
  appearance: none;
  background: transparent;
  cursor: pointer;
  touch-action: none;
}

/* 轨道：色相条用彩虹，透明度条用「棋盘格 + 到当前色的渐变」 */
.np-colorpicker__slider::-webkit-slider-runnable-track {
  height: var(--np-picker-track-h);
  border-radius: 3px;
}

.np-colorpicker__slider::-moz-range-track {
  height: var(--np-picker-track-h);
  border-radius: 3px;
}

/* 滑块做成竖条，压在轨道上 */
.np-colorpicker__slider::-webkit-slider-thumb {
  appearance: none;
  width: var(--np-picker-thumb-w);
  height: var(--np-picker-strip-h);
  /* 竖条比轨道高，往上垫半个差值 → 正好压在轨道正中（原来手写成 -2px） */
  margin-top: calc((var(--np-picker-track-h) - var(--np-picker-strip-h)) / 2);
  border: 2px solid var(--np-picker-handle-ring);
  border-radius: 3px;
  background: transparent;
  box-shadow: var(--np-picker-handle-outline);
}

.np-colorpicker__slider::-moz-range-thumb {
  width: var(--np-picker-thumb-w);
  height: var(--np-picker-strip-h);
  border: 2px solid var(--np-picker-handle-ring);
  border-radius: 3px;
  background: transparent;
  box-shadow: var(--np-picker-handle-outline);
}

/*
 * ⚠️ **色相条这一对不是主题色，是色相环本身**：`#f00 → #ff0 → #0f0 …`
 * 是 HSV 里 h 从 0 走到 360 的采样点，改任何一个都不是「换个配色」，
 * 而是「色相条在说谎」—— 条上的位置跟取到的颜色对不上。
 *
 * ⚠️⚠️ 而且 webkit / moz 这两条**必须分开写**，不能合成
 * `.np-colorpicker__hue::-webkit-slider-runnable-track, …::-moz-range-track`：
 * 选择器列表里只要有一个引擎不认识的伪元素，**整条规则都会被丢掉** ——
 * 合并的结果是两个引擎的轨道一起消失（而不是各自命中各自那一半）。
 */
.np-colorpicker__hue::-webkit-slider-runnable-track {
  background: linear-gradient(
    to right,
    #f00,
    #ff0 17%,
    #0f0 33%,
    #0ff 50%,
    #00f 67%,
    #f0f 83%,
    #f00
  );
}

.np-colorpicker__hue::-moz-range-track {
  background: linear-gradient(
    to right,
    #f00,
    #ff0 17%,
    #0f0 33%,
    #0ff 50%,
    #00f 67%,
    #f0f 83%,
    #f00
  );
}

/* ⚠️ 同色相条：这两条也必须分开写（理由见上面那条注释） */
.np-colorpicker__alpha::-webkit-slider-runnable-track {
  background-image:
    linear-gradient(to right, transparent, var(--np-picker-color, #fff)),
    var(--np-picker-checker);
  /*
   * ⚠️ 这里两个坑都踩过，别改错：
   *
   * 1. 渐变那层的尺寸必须是 **100% 100%**。写成 12px 12px 的话，
   *    渐变会在 12px 的方框里画完然后**平铺** —— 「透明 → 颜色」的
   *    过渡会每 12px 重复一次，整条轨道变成一串锯齿。
   * 2. 尺寸和位置的**个数必须跟图层一一对应**（1 个渐变 + 4 个棋盘格 = 5 层）。
   *    多给一个值就会整体错位，棋盘格看起来变成条纹。
   *    ⚠️ 这两份列表现在都在 `--np-picker-checker-sizes` /
   *    `--np-picker-checker-pos` 里算好（在 `.np-colorpicker` 上），
   *    **别在下面手写列表** —— 手写就要重新数一遍图层，数错就是这个坑。
   */
  background-color: var(--np-checker-a);
  background-size: 100% 100%, var(--np-picker-checker-sizes);
  background-position: 0 0, var(--np-picker-checker-pos);
}

.np-colorpicker__alpha::-moz-range-track {
  background-image:
    linear-gradient(to right, transparent, var(--np-picker-color, #fff)),
    var(--np-picker-checker);
  /*
   * ⚠️ 这里两个坑都踩过，别改错：
   *
   * 1. 渐变那层的尺寸必须是 **100% 100%**。写成 12px 12px 的话，
   *    渐变会在 12px 的方框里画完然后**平铺** —— 「透明 → 颜色」的
   *    过渡会每 12px 重复一次，整条轨道变成一串锯齿。
   * 2. 尺寸和位置的**个数必须跟图层一一对应**（1 个渐变 + 4 个棋盘格 = 5 层）。
   *    多给一个值就会整体错位，棋盘格看起来变成条纹。
   *    ⚠️ 这两份列表现在都在 `--np-picker-checker-sizes` /
   *    `--np-picker-checker-pos` 里算好（在 `.np-colorpicker` 上），
   *    **别在下面手写列表** —— 手写就要重新数一遍图层，数错就是这个坑。
   */
  background-color: var(--np-checker-a);
  background-size: 100% 100%, var(--np-picker-checker-sizes);
  background-position: 0 0, var(--np-picker-checker-pos);
}

.np-colorpicker__foot {
  display: flex;
  align-items: center;
  gap: 7px;
}

.np-colorpicker__preview {
  position: relative;
  flex: 0 0 auto;
  width: 24px;
  height: var(--np-ctl-h-sm, 22px);
  border: 1px solid var(--np-widget-border, rgba(0, 0, 0, 0.6));
  border-radius: var(--np-radius-s, 4px);
  overflow: hidden;
  /* 同上：给死基色，别让背后的东西透上来 */
  background-color: var(--np-checker-a);
  background-image: var(--np-picker-checker);
  background-size: var(--np-picker-checker-sizes);
  background-position: var(--np-picker-checker-pos);
}

.np-colorpicker__preview i {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.np-colorpicker__hex {
  flex: 1;
  min-width: 0;
  height: var(--np-ctl-h-sm, 22px);
  padding: 0 6px;
  border: 1px solid var(--np-widget-border, rgba(0, 0, 0, 0.6));
  border-radius: var(--np-radius-s, 4px);
  background: var(--np-widget-bg, #383838);
  color: inherit;
  /* 等宽字体栈没有令牌（全仓库都是就地写），十六进制要等宽才对得齐 */
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  /*
   * ⚠️ 这条的兜底**不是装饰**：`--np-font-size` 和上面的 `--np-widget-*`
   * 一样声明在 `.np-designer, .np-node` 上，而拾色器是 Teleport 到 body 的
   * 浮层 —— 不在那棵子树里，读不到就只能用兜底值（= 深色主题那一份）。
   * 反过来，声明在 `:root` 上的令牌（`--np-ctl-h-sm` / `--np-checker-*`…）
   * 一律不写兜底：兜底是第二份抄本，正是这一轮要消掉的东西。
   */
  font-size: var(--np-font-size, 11px);
  text-transform: lowercase;
}

.np-colorpicker__hex:focus {
  outline: none;
  border-color: var(--np-accent, #f0a04b);
}

.np-colorpicker__percent {
  flex: 0 0 auto;
  min-width: 32px;
  /*
   * ⚠️ 用**面板族**的令牌，不用 `--np-text-dim`：后者声明在
   * `.np-designer, .np-node` 上，Teleport 出来的浮层继承不到 —— 深色表里
   * 它没有 `:root` 副本，那样写等于永远用兜底值（浅色主题也翻不过来）。
   * `--np-panel-dim` 在 `:root` 上，浅色主题有对应覆盖。
   */
  color: var(--np-panel-dim);
  font-variant-numeric: tabular-nums;
  text-align: right;
}
</style>
